import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Text, Flex, Image
} from 'components/ui';
import CompareZoneBase from 'components/Catalog/ProductGrid/CompareModal/Zones/CompareZoneBase';
import { processComparisonValue } from 'components/Catalog/ProductGrid/hooks/useComparisonSkus';
import EmptyState from 'components/Catalog/ProductGrid/CompareModal/EmptyState';
import skuUtils from 'utils/Sku';
import { radii, fontSizes, lineHeights } from 'style/config';

const ColorsZone = ({ skus = [], getText = () => {} }) => {
    const [multiLineMap, setMultiLineMap] = React.useState({});
    const refCallbacksRef = React.useRef({});

    React.useEffect(() => {
        refCallbacksRef.current = {};
        setMultiLineMap({});
    }, [skus]);

    const getMeasureRef = React.useCallback(
        index => {
            if (!refCallbacksRef.current[index]) {
                refCallbacksRef.current[index] = node => {
                    if (node && typeof window !== 'undefined') {
                        const lineHeightPx = parseFloat(window.getComputedStyle(node).lineHeight);

                        if (!lineHeightPx) {
                            return;
                        }

                        const isMultiLine = Math.round(node.scrollHeight / lineHeightPx) > 1;
                        setMultiLineMap(prev => (prev[index] === isMultiLine ? prev : { ...prev, [index]: isMultiLine }));
                    }
                };
            }

            return refCallbacksRef.current[index];
        },
        [setMultiLineMap, refCallbacksRef]
    );

    const renderContent = (sku, index) => {
        const valueData = processComparisonValue(
            sku,
            skuItem => skuItem.color,
            (validatedSku, parentProduct, uniqueColors) => {
                // Check if this is a color variation product
                const variationType = parentProduct.variationType?.toLowerCase();
                const isColorProduct = variationType === skuUtils.skuVariationType.COLOR.toLowerCase();
                const hasColorValue = !!validatedSku.color;

                return (isColorProduct || hasColorValue) && uniqueColors.size > 0;
            }
        );

        if (!valueData) {
            return <EmptyState />;
        }

        const { currentValue: colorName, additionalValues: additionalColors, validatedSku } = valueData;
        const colorImage = validatedSku?.swatchImageUrl || validatedSku?.primaryImages?.[0]?.baseUrl;
        const colorDescription = validatedSku?.variationDesc;

        return (
            <Flex
                flexDirection='column'
                flex={1}
                minHeight={0}
                width='100%'
                gap={1}
                role='group'
                aria-label={getText('productInformation', [index + 1])}
            >
                <Flex
                    flexDirection='column'
                    flex={1}
                    minHeight={0}
                >
                    <Flex
                        gap={2}
                        alignItems={multiLineMap[index] === false ? 'center' : 'flex-start'}
                    >
                        {colorImage && (
                            <Box css={styles.colorThumbnail}>
                                <Image
                                    src={colorImage}
                                    alt={colorName || ''}
                                    disableLazyLoad={true}
                                    objectFit='cover'
                                />
                            </Box>
                        )}
                        {colorName && (
                            <Text
                                ref={getMeasureRef(index)}
                                fontSize='sm'
                                css={styles.colorName}
                                numberOfLines={2}
                                is='p'
                            >
                                {colorName}
                                {colorDescription && ` - ${colorDescription}`}
                            </Text>
                        )}
                    </Flex>
                </Flex>
                {additionalColors > 0 && (
                    <Text
                        flexShrink={0}
                        fontSize='sm'
                        color='gray'
                        is='p'
                    >
                        + {additionalColors} {additionalColors === 1 ? getText('color').toLowerCase() : getText('colors').toLowerCase()}
                    </Text>
                )}
            </Flex>
        );
    };

    return (
        <CompareZoneBase
            skus={skus}
            getText={getText}
            titleKey='colors'
            renderContent={renderContent}
        />
    );
};

const styles = {
    colorThumbnail: {
        width: '24px',
        height: '24px',
        img: {
            borderRadius: `${radii.full}px`
        }
    },
    colorName: {
        flex: '1 0 0',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        lineHeight: lineHeights.sm,
        fontSize: `${fontSizes.sm}px`
    }
};

ColorsZone.propTypes = {
    skus: PropTypes.arrayOf(PropTypes.object),
    getText: PropTypes.func
};

export default wrapFunctionalComponent(ColorsZone, 'ColorsZone');
